import React from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { IconReload, IconStack2, IconX } from "@tabler/icons-react";
import ButtonMenu from "components/ButtonMenu";
import clsx from "clsx";
import { searchStore } from "features/ProductSearch/SearchStore";
import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import { useMappingTools } from "features/MappingEditor/useMappingTools";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { COEStore } from "../ComponentEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import useToast from "hooks/useToast";
import { RoleEnum } from "api-client";

interface SaveLoadingProps {
  state: boolean;
  match?: boolean;
}

export default observer(function EditorHeader({
  saveAndClose,
  upgradeComponent,
}: {
  saveAndClose: (match?: boolean) => Promise<void>;
  upgradeComponent?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const toast = useToast();
  const [saveLoading, setSaveLoading] = React.useState<SaveLoadingProps>({
    state: false,
    match: false,
  });
  const [upgradeLoading, setUpgradeLoading] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { onCloseEditor } = useMappingTools();

  const isEditMode = location.state?.mode === "edit";
  const hasOutdatedChildren = Boolean(
    searchStore.componentDetails?.has_outdated_children
  );

  const isAdmin =
    sidebarMenuStore.currentOrganisation?.role === RoleEnum.NUMBER_1;

  const showUpgradeButton = isEditMode && hasOutdatedChildren;

  const buttonItems = [
    {
      name: t("productEditor.saveAndMapp"),
      onClick: () => onClickMenu(t("productEditor.saveAndMapp"), true),
      disabled:
        COEStore.saveValidationErrors ||
        MEStore.isCurrentFileMappingInProgress() ||
        saveLoading.state,
    },
    {
      name: t("commons.save"),
      onClick: () => onClickMenu(t("commons.save")),
      disabled:
        COEStore.saveValidationErrors ||
        MEStore.isCurrentFileMappingInProgress() ||
        saveLoading.state,
    },
  ];

  const [clickedButton, setClickedButton] = React.useState<string>(
    buttonItems[0].name
  );

  const onClickMenu = async (menuName: string, match?: boolean) => {
    setSaveLoading({ state: true, match: match });
    setClickedButton(menuName);
    await saveAndClose(match);
    setSaveLoading({ state: false });
  };

  const handleUpgrade = async () => {
    if (!upgradeComponent) return;
    setUpgradeLoading(true);
    try {
      await upgradeComponent();
    } catch {
      toast(t("components.warningMsgOnUpgrade"), "warning");
    } finally {
      setConfirmOpen(false);
      setUpgradeLoading(false);
    }
  };

  const cancelButton = () => {
    return (
      <div
        onClick={() => onCloseEditor()}
        className={clsx(
          "border border-gray-300 rounded-md min-w-9 min-h-9 flex items-center justify-center",
          "ml-3 hover:bg-gray-100 text-gray-700 hover:text-gray-900 cursor-pointer",
          {
            "opacity-50 !cursor-not-allowed pointer-events-none":
              MEStore.isCurrentFileMappingInProgress() || saveLoading.state,
          }
        )}
      >
        <IconX className="min-w-6 min-h-6" stroke={2} />
      </div>
    );
  };

  const showSaveButtons = () => {
    const showSaveButtonOnly =
      (searchStore.usingOrganisationSearch &&
        !location.pathname.includes("mapping-editor")) ||
      location.state?.isDetails;

    if (showSaveButtonOnly) {
      return (
        <Button
          onClick={() => onClickMenu(t("commons.save"))}
          disable={buttonItems[1].disabled}
          className="!h-9"
          tooltip={
            buttonItems[1].disabled ? t("productEditor.saveButtonTooltip") : ""
          }
        >
          {t("commons.save")}
        </Button>
      );
    }

    return (
      <>
        <ButtonMenu
          current={buttonItems.find((b) => b.name === clickedButton)}
          items={buttonItems}
          loading={saveLoading.state}
          disabled={buttonItems[1].disabled}
          tooltip={
            buttonItems[1].disabled ? t("productEditor.saveButtonTooltip") : ""
          }
          className="flex lg:hidden !h-9"
          buttonClassName="!h-9"
        />
        <Button
          key={buttonItems[1].name}
          type="secondary"
          onClick={buttonItems[1].onClick}
          loading={saveLoading.state && !saveLoading.match}
          disable={buttonItems[1].disabled}
          tooltip={
            buttonItems[1].disabled ? t("productEditor.saveButtonTooltip") : ""
          }
          className="hidden lg:flex !h-9"
        >
          {buttonItems[1].name}
        </Button>
        <Button
          key={buttonItems[0].name}
          width="fit-content"
          className="ml-3 hidden lg:flex !h-9"
          onClick={buttonItems[0].onClick}
          loading={saveLoading.state && saveLoading.match}
          disable={buttonItems[0].disabled}
          tooltip={
            buttonItems[0].disabled ? t("productEditor.saveButtonTooltip") : ""
          }
        >
          {buttonItems[0].name}
        </Button>
      </>
    );
  };

  return (
    <div
      className={clsx(
        "flex w-full px-6 py-3 items-center justify-between border-b border-gray-100 bg-cyan-50 h-[60px]"
      )}
    >
      <div id="editor-title" className="flex">
        <div className="flex justify-between items-center">
          <IconStack2
            stroke={2}
            className="bg-cyan-300 w-8 h-8 rounded-full p-1.5 mr-3"
          />
          <div className="text-xl font-semibold text-black mr-6">
            {t("components.title")}
          </div>
        </div>
      </div>
      <div className="flex items-center">
        {showUpgradeButton && (
          <Button
            leadingIcon={<IconReload />}
            className="!h-9 mr-3"
            onClick={() => setConfirmOpen(true)}
            loading={upgradeLoading}
            disable={
              !isAdmin ||
              COEStore.saveValidationErrors ||
              MEStore.isCurrentFileMappingInProgress() ||
              saveLoading.state
            }
            tooltip={
              !isAdmin
                ? t("components.upgradeAdminOnly")
                : COEStore.saveValidationErrors
                ? t("productEditor.saveButtonTooltip")
                : ""
            }
          >
            {t("components.upgradeComponent")}
          </Button>
        )}
        {showSaveButtons()}
        {cancelButton()}
      </div>
      <ConfirmModal
        open={confirmOpen}
        close={() => setConfirmOpen(false)}
        title={t("components.confirmUpdateTitle")}
        descriptionMesage={t("components.confirmUpdateBody")}
        confirmButtonTitle={t("components.upgradeComponent")}
        onConfirm={handleUpgrade}
        type="info"
      />
    </div>
  );
});
